/* ============================================================================
   errors.ts — what a failure IS on this client.

   The server never sends a user-facing sentence. It sends a stable CODE, structured
   DETAILS and a CORRELATION ID (Housekeeping.Application.Common.AppError). The code is
   also the translation key, so there is one vocabulary rather than two that drift.

   Two failure kinds are kept apart here, and the distinction is not academic:

     ApiError        the server answered, and said no. There is a code to translate
                     and, usually, something the user can do about it.
     TransportError  the server did not answer at all. There is no code, because
                     nothing on the server formed an opinion.

   Collapsing them is the mistake. An attendant in a service corridor with no signal
   needs the offline screen and a queued action, not "you do not have permission" —
   and a screen that renders every failure through the same message catalogue will
   eventually tell them exactly that.
   ========================================================================= */

export interface ErrorEnvelope {
  code: string
  details?: Record<string, string> | null
  correlationId: string
}

export class ApiError extends Error {
  readonly code: string
  readonly details: Record<string, string>
  readonly correlationId: string
  readonly status: number

  constructor(envelope: ErrorEnvelope, status: number) {
    // The message is for a developer reading a stack trace. It is NEVER shown to a
    // user: users get translateError(), which is why the code travels separately.
    super(`${envelope.code} (HTTP ${status})`)
    this.name = 'ApiError'
    this.code = envelope.code
    this.details = envelope.details ?? {}
    this.correlationId = envelope.correlationId
    this.status = status
  }

  /** Codes are dotted and hierarchical so a caller can handle a whole family
   *  ("room.transition.*") without enumerating every member of it. */
  isFamily(prefix: string): boolean {
    return this.code === prefix || this.code.startsWith(prefix + '.')
  }
}

/** The server was not reached, or did not answer in a way we can read. */
export class TransportError extends Error {
  readonly cause?: unknown
  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'TransportError'
    this.cause = cause
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError
export const isTransportError = (e: unknown): e is TransportError => e instanceof TransportError
